import { memo, useMemo, useState, useCallback } from 'react';
import {
  Badge,
  Button,
  Card,
  Code,
  Group,
  Stack,
  Text,
  Title,
  Flex,
  Loader,
  Box,
} from '@mantine/core';
import { RefreshCw, ArrowUp, ArrowDown, ArrowUpDown } from 'lucide-react';
import { format } from 'date-fns';
import { it } from 'date-fns/locale';
import { notifications } from '@mantine/notifications';
import {
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
  ColumnDef,
  SortingState,
} from '@tanstack/react-table';
import type { Device, DeviceDYNCode } from '@types';

interface DeviceTokenTableProps {
  devices: Device[];
  deviceTokens: DeviceDYNCode[];
  isLoading: boolean;
  error: string | null;
  onRefresh: () => void;
  onTokenInvalidated?: () => void;
  onInvalidateToken: (token: DeviceDYNCode) => Promise<void>;
}

export const DeviceTokenTable = memo<DeviceTokenTableProps>(
  ({
    devices,
    deviceTokens,
    isLoading,
    error,
    onRefresh,
    onTokenInvalidated,
    onInvalidateToken,
  }) => {
    const [sorting, setSorting] = useState<SortingState>([]);

    const getDeviceName = (deviceCode: string) => {
      const device = devices.find(d => d.code === deviceCode);
      return device?.name || device?.model || `Dispositivo ${deviceCode}`;
    };

    const handleInvalidateToken = useCallback(
      async (token: DeviceDYNCode) => {
        try {
          await onInvalidateToken(token);
          notifications.show({
            color: 'teal',
            withBorder: true,
            radius: 'lg',
            title: 'Token Invalidato',
            autoClose: 2000,
            message: 'Il token è stato invalidato con successo',
          });
          onRefresh();
          onTokenInvalidated?.();
        } catch {
          notifications.show({
            color: 'red',
            withBorder: true,
            radius: 'lg',
            title: 'Errore',
            autoClose: 3000,
            message: "Errore durante l'invalidazione del token",
          });
        }
      },
      [onInvalidateToken, onRefresh, onTokenInvalidated]
    );

    // const handleInvalidateAll = async () => {
    //   setInvalidatingAll(true);
    //   try {
    //     if (sessionTokens.length === 0) {
    //       notifications.show({
    //         color: 'yellow',
    //         withBorder: true,
    //         radius: 'lg',
    //         title: 'Nessun token',
    //         autoClose: 2000,
    //         message: 'Non ci sono token da invalidare',
    //       });
    //       return;
    //     }

    //     const grouped = groupBy(
    //       sessionTokens.map(token => ({
    //         ...token,
    //         deviceId:
    //           token.deviceId ??
    //           devices.find(d => d.code === token.deviceCode)?.id?.toString() ??
    //           '',
    //       })),
    //       token => token.deviceId ?? ''
    //     );

    //     const groups = values(grouped).filter(bucket => bucket.length > 0);
    //     await Promise.all(groups.map(bucket => onInvalidateTokens(bucket)));

    //     notifications.show({
    //       color: 'teal',
    //       withBorder: true,
    //       radius: 'lg',
    //       title: 'Tutti i token invalidati',
    //       autoClose: 2000,
    //       message: `${sessionTokens.length} token invalidati correttamente`,
    //     });

    //     onRefresh();
    //     onTokenInvalidated?.();
    //   } catch {
    //     notifications.show({
    //       color: 'red',
    //       withBorder: true,
    //       radius: 'lg',
    //       title: 'Errore',
    //       autoClose: 3000,
    //       message: "Errore durante l'invalidazione di tutti i token",
    //     });
    //   } finally {
    //     setInvalidatingAll(false);
    //   }
    // };

    const appLabelResolver = useMemo(
      () => ({
        1: 'Augmented Life Experience',
        2: 'Augmented Life Suite',
      }),
      []
    );

    const resolveAppLabel = (appId?: number) => {
      if (!appId) return 'N/D';
      return (
        appLabelResolver[appId as keyof typeof appLabelResolver] ||
        `App ${appId}`
      );
    };

    const resolveTokenAppLabel = (token: DeviceDYNCode) =>
      resolveAppLabel(token.appId ?? token.sessionInfo?.appId);

    const columns = useMemo<ColumnDef<DeviceDYNCode>[]>(
      () => [
        {
          id: 'sessionId',
          header: 'ID Sessione',
          cell: ({ row }) => {
            const sessionId =
              row.original.sessionInfo?.sessionId ||
              row.original.session?.split('#')[0] ||
              row.original.deviceToken ||
              row.original.code ||
              'N/D';
            return <Code>{sessionId.slice(0, 8)}</Code>;
          },
          enableSorting: false,
        },
        {
          id: 'device',
          header: 'Dispositivo',
          cell: ({ row }) => {
            const token = row.original;
            return (
              <Stack gap={0}>
                <Text size='sm' fw={500}>
                  {getDeviceName(token.deviceCode)}
                </Text>
                <Text size='xs' c='dimmed'>
                  {token.deviceCode}
                </Text>
              </Stack>
            );
          },
          enableSorting: true,
          sortingFn: (rowA, rowB) => {
            const deviceNameA = getDeviceName(
              rowA.original.deviceCode
            ).toLowerCase();
            const deviceNameB = getDeviceName(
              rowB.original.deviceCode
            ).toLowerCase();
            return deviceNameA.localeCompare(deviceNameB);
          },
        },
        {
          id: 'status',
          header: 'Stato Sessione',
          cell: ({ row }) => {
            const token = row.original;
            const expiresAtMs =
              token.expiresAt && token.expiresAt > 0
                ? token.expiresAt * 1000
                : 0;
            const isExpired =
              expiresAtMs > 0 ? expiresAtMs <= Date.now() : false;
            return (
              <Badge
                color={isExpired ? 'red' : 'green'}
                variant='light'
                size='sm'
              >
                {isExpired ? 'Scaduta' : 'Attiva'}
              </Badge>
            );
          },
          enableSorting: true,
          sortingFn: (rowA, rowB) => {
            const expiresAtA =
              rowA.original.expiresAt && rowA.original.expiresAt > 0
                ? rowA.original.expiresAt * 1000
                : 0;
            const expiresAtB =
              rowB.original.expiresAt && rowB.original.expiresAt > 0
                ? rowB.original.expiresAt * 1000
                : 0;
            const isExpiredA =
              expiresAtA > 0 ? expiresAtA <= Date.now() : false;
            const isExpiredB =
              expiresAtB > 0 ? expiresAtB <= Date.now() : false;
            // Attiva (false) viene prima di Scaduta (true)
            return Number(isExpiredA) - Number(isExpiredB);
          },
        },
        {
          id: 'app',
          header: 'App',
          cell: ({ row }) => {
            const token = row.original;
            return (
              <Stack gap={0}>
                <Text size='sm'>{resolveTokenAppLabel(token)}</Text>
                {(token.appId ?? token.sessionInfo?.appId) && (
                  <Text size='xs' c='dimmed'>
                    ID: {token.appId ?? token.sessionInfo?.appId}
                  </Text>
                )}
              </Stack>
            );
          },
          enableSorting: true,
          sortingFn: (rowA, rowB) => {
            const appLabelA = resolveTokenAppLabel(rowA.original).toLowerCase();
            const appLabelB = resolveTokenAppLabel(rowB.original).toLowerCase();
            return appLabelA.localeCompare(appLabelB);
          },
        },
        {
          id: 'expiresAt',
          header: 'Scadenza',
          cell: ({ row }) => {
            const token = row.original;
            const expiresAtMs =
              token.expiresAt && token.expiresAt > 0
                ? token.expiresAt * 1000
                : 0;
            return (
              <Text size='xs'>
                {format(new Date(expiresAtMs), "dd/MM/yyyy 'alle' HH:mm", {
                  locale: it,
                })}
              </Text>
            );
          },
          enableSorting: false,
        },
        {
          id: 'actions',
          header: 'Azioni',
          cell: ({ row }) => {
            return (
              <Button
                variant='light'
                color='red'
                size='xs'
                onClick={() => handleInvalidateToken(row.original)}
              >
                Invalida
              </Button>
            );
          },
          enableSorting: false,
        },
      ],
      [devices, handleInvalidateToken, resolveTokenAppLabel, appLabelResolver]
    );

    const table = useReactTable({
      data: deviceTokens,
      columns,
      state: {
        sorting,
      },
      onSortingChange: setSorting,
      getCoreRowModel: getCoreRowModel(),
      getSortedRowModel: getSortedRowModel(),
    });

    if (isLoading) {
      return (
        <Card withBorder p='md'>
          <Flex justify='center' align='center' h={200}>
            <Stack align='center' gap='md'>
              <Loader size='lg' />
              <Text>Caricamento device tokens...</Text>
            </Stack>
          </Flex>
        </Card>
      );
    }

    if (error) {
      return (
        <Card withBorder p='md'>
          <Text c='red'>Errore nel caricamento dei device tokens: {error}</Text>
        </Card>
      );
    }

    return (
      <Card withBorder p='md'>
        <Stack gap='md'>
          <Group justify='space-between' align='center'>
            <Stack gap={2}>
              <Title order={3}>Lista sessioni dei dispositivi attive</Title>
              <Text size='sm' c='dimmed'>
                Le sessioni attive mantengono il visore collegato, così non devi
                rifare il login ogni volta
              </Text>
            </Stack>
            <Group gap='xs'>
              <Button
                variant='light'
                color='blue'
                size='sm'
                leftSection={<RefreshCw size={16} />}
                loading={isLoading}
                onClick={onRefresh}
              >
                Aggiorna
              </Button>
            </Group>
          </Group>

          {deviceTokens.length > 0 ? (
            <Box
              style={{
                border: '1px solid #e9ecef',
                borderRadius: '8px',
                overflow: 'hidden',
              }}
            >
              <table
                style={{
                  width: '100%',
                  borderCollapse: 'collapse',
                }}
              >
                <thead style={{ backgroundColor: '#f8f9fa' }}>
                  {table.getHeaderGroups().map(headerGroup => (
                    <tr key={headerGroup.id}>
                      {headerGroup.headers.map(header => (
                        <th
                          key={header.id}
                          style={{
                            padding: '12px 16px',
                            textAlign: 'left',
                            fontWeight: 600,
                            fontSize: '14px',
                            borderBottom: '1px solid #e9ecef',
                            cursor: header.column.getCanSort()
                              ? 'pointer'
                              : 'default',
                          }}
                          onClick={header.column.getToggleSortingHandler()}
                        >
                          <Flex align='center' gap='xs'>
                            {flexRender(
                              header.column.columnDef.header,
                              header.getContext()
                            )}
                            {header.column.getCanSort() && (
                              <span
                                style={{
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                }}
                              >
                                {header.column.getIsSorted() === 'asc' && (
                                  <ArrowUp size={14} />
                                )}
                                {header.column.getIsSorted() === 'desc' && (
                                  <ArrowDown size={14} />
                                )}
                                {!header.column.getIsSorted() && (
                                  <ArrowUpDown
                                    size={14}
                                    style={{ opacity: 0.3 }}
                                  />
                                )}
                              </span>
                            )}
                          </Flex>
                        </th>
                      ))}
                    </tr>
                  ))}
                </thead>
                <tbody>
                  {table.getRowModel().rows.map((row, index) => (
                    <tr
                      key={
                        row.original.deviceToken ||
                        row.original.code ||
                        row.original.session ||
                        `${row.original.deviceCode}-${index}`
                      }
                      style={{
                        borderBottom: '1px solid #f1f3f4',
                        transition: 'background-color 0.2s',
                        backgroundColor:
                          index % 2 === 0 ? '#ffffff' : '#f8f9fa',
                      }}
                      onMouseEnter={e => {
                        e.currentTarget.style.backgroundColor = '#f0f0f0';
                      }}
                      onMouseLeave={e => {
                        e.currentTarget.style.backgroundColor =
                          index % 2 === 0 ? '#ffffff' : '#f8f9fa';
                      }}
                    >
                      {row.getVisibleCells().map(cell => (
                        <td
                          key={cell.id}
                          style={{
                            padding: '12px 16px',
                            fontSize: '14px',
                          }}
                        >
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext()
                          )}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </Box>
          ) : (
            <Stack
              align='center'
              justify='center'
              py={48}
              style={{
                minHeight: 220,
                background: '#f8f9fa',
                borderRadius: 12,
                border: '1px solid #edf2f5',
              }}
            >
              <Text size='md' c='dimmed' fw={500}>
                Nessuna sessione trovata
              </Text>
              <Text size='sm' c='dimmed' ta='center'>
                Non ci sono token sessione attivi per questo utente al momento.
              </Text>
            </Stack>
          )}
        </Stack>
      </Card>
    );
  }
);

DeviceTokenTable.displayName = 'DeviceTokenTable';
